import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function ExternalSetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setInfoError("No invitation token provided"); setLoadingInfo(false); return; }
    api.get(`/auth/setup-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setInfo(data))
      .catch(e => setInfoError(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/external-setup", { token, password });
      if (data?.token) localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data));
      setUser(data);
      toast.success(`Welcome to CargoDash, ${data.name}!`);
      navigate("/");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8" data-testid="external-setup-card">
        <div className="text-center mb-6">
          <img src="/cargodash-logo.svg" alt="CargoDash" className="h-10 mx-auto mb-2" />
          <p className="text-slate-400 text-xs uppercase tracking-[0.2em]">Set your password</p>
        </div>

        {loadingInfo ? (
          <div className="text-center text-slate-500 py-8">Verifying invitation…</div>
        ) : infoError ? (
          <div className="text-center py-8" data-testid="setup-error-block">
            <AlertTriangle className="mx-auto mb-3 text-amber-500" size={32} />
            <p className="text-slate-700 font-medium">{infoError}</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 text-sm text-teal-600 underline"
            >Go to login</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="external-setup-form">
            <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-sm flex items-start gap-2">
              <ShieldCheck size={16} className="text-teal-600 mt-0.5" />
              <div>
                <p className="text-slate-700">Hi <strong>{info.name || info.email}</strong>,</p>
                <p className="text-slate-500 text-xs mt-1">{info.email}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">New password</Label>
              <div className="relative">
                <Input
                  data-testid="external-setup-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="h-11 pr-10"
                />
                <button type="button" onClick={() => setShow(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500">Confirm password</Label>
              <Input
                data-testid="external-setup-confirm"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                className="h-11"
              />
            </div>

            {err && (
              <p data-testid="setup-form-error" className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-2">{err}</p>
            )}

            <Button
              data-testid="external-setup-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            >{submitting ? "Setting up…" : "Set password & sign in"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
